import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import aiService from '../../api/aiService';
import { GRID_CELL_SIZE_M, DEFAULT_MODULE_CELLS_WIDE, DEFAULT_MODULE_CELLS_LONG, OBJECT_TYPES } from '../configuratorConstants';
import { validateProjectState, generateId } from '../configuratorUtils';
import { useTranslation } from 'react-i18next';

const HISTORY_STORAGE_KEY = 'rebbud_project_history';
const MAX_HISTORY_ITEMS = 20;

export const useConfiguratorActions = (dispatch, state, uiStateSetters, availableTemplates = []) => {
    const loadFileInputRef = useRef(null);
    const { setIsTemplatePanelOpen, setActivePlacementSlot } = uiStateSetters;
    const { t } = useTranslation();

    const saveProjectToHistory = useCallback(() => {
        try {
            const history = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || '[]');
            
            let moduleCount = 0;
            Object.values(state.projectObjects).forEach(floorObjects => {
                moduleCount += floorObjects.filter(obj => obj.type === OBJECT_TYPES.MODULE).length;
            });
            const floorCount = state.floors.length;
            const preview = `${moduleCount} ${t('history.modules_few', { count: moduleCount })}, ${floorCount} ${t('history.floors_few', { count: floorCount })}`;

            const newHistoryItem = {
                id: generateId('hist_'),
                timestamp: Date.now(),
                preview: preview,
                projectData: state,
            };

            const updatedHistory = [newHistoryItem, ...history].slice(0, MAX_HISTORY_ITEMS);
            
            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
            toast.success(t('toasts.projectSavedToHistory'));

        } catch (error) {
            console.error("Failed to save project to history", error);
            toast.error(t('toasts.projectSaveError'));
        }
    }, [state, t]);

    const handleAiGeneratedModules = useCallback((aiModules, notes) => {
        const newModuleConfigs = aiModules.map(aiModuleData => {
            const templateFromAiId = aiModuleData.templateId;

            // Пытаемся найти реальный шаблон серед доступних у панелі за ID з AI
            const matchedTemplate = availableTemplates.find(t => {
                const rawConfig = typeof t.config === 'string' ? JSON.parse(t.config) : (t.config || {});
                const templateConfigId = rawConfig.id || t.id;
                return String(templateConfigId) === String(templateFromAiId);
            });

            let baseConfig;

            if (matchedTemplate) {
                const rawConfig = typeof matchedTemplate.config === 'string'
                    ? JSON.parse(matchedTemplate.config)
                    : (matchedTemplate.config || {});

                baseConfig = {
                    id: rawConfig.id || matchedTemplate.id,
                    cellsWide: parseInt(rawConfig.cellsWide) || DEFAULT_MODULE_CELLS_WIDE,
                    cellsLong: parseInt(rawConfig.cellsLong) || DEFAULT_MODULE_CELLS_LONG,
                    label: rawConfig.label || matchedTemplate.name || t('ai.defaultModuleName'),
                    internalWallSegments: rawConfig.internalWallSegments || {},
                    predefinedElements: rawConfig.predefinedElements || [],
                    freeElements: rawConfig.freeElements || [],
                    rotation: parseInt(rawConfig.rotation) || 0,
                    subtype: rawConfig.subtype,
                };
            } else {
                // Фолбек: якщо шаблон не знайдено, працюємо як раніше
                baseConfig = {
                    id: templateFromAiId,
                    cellsWide: parseInt(aiModuleData.cellsWide) || DEFAULT_MODULE_CELLS_WIDE,
                    cellsLong: parseInt(aiModuleData.cellsLong) || DEFAULT_MODULE_CELLS_LONG,
                    label: aiModuleData.label || t('ai.defaultModuleName'),
                    internalWallSegments: aiModuleData.internalWallSegments || {},
                    predefinedElements: aiModuleData.predefinedElements || [],
                    freeElements: aiModuleData.freeElements || [],
                    rotation: parseInt(aiModuleData.rotation) || 0,
                    subtype: aiModuleData.subtype,
                };
            }

            // Якщо ШІ явно прислав свої predefined/free elements – дозволимо їх перевизначити
            const predefinedElements = (aiModuleData.predefinedElements && aiModuleData.predefinedElements.length > 0)
                ? aiModuleData.predefinedElements
                : baseConfig.predefinedElements || [];

            const freeElements = (aiModuleData.freeElements && aiModuleData.freeElements.length > 0)
                ? aiModuleData.freeElements
                : baseConfig.freeElements || [];

            const moduleConfig = {
                ...baseConfig,
                posX: parseFloat(aiModuleData.posX) || 0,
                posY: parseFloat(aiModuleData.posY) || 0,
                rotation: parseInt(aiModuleData.rotation) || baseConfig.rotation || 0,
                predefinedElements,
                freeElements,
            };

            moduleConfig.posX = Math.round(moduleConfig.posX / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
            moduleConfig.posY = Math.round(moduleConfig.posY / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
            return moduleConfig;
        });

        newModuleConfigs.forEach(config => {
            dispatch({ type: 'ADD_MODULE', payload: { moduleConfig: config } });
        });

        if (notes) toast.info(`${t('ai.notes')}: ${notes}`, { autoClose: 7000 });
    }, [availableTemplates, dispatch, t]);

    const submitAiQuery = useCallback(async (aiQuery) => {
        if (!validateProjectState(state.projectObjects, state.floors, t)) {
            return;
        }

        if (!aiQuery.trim()) {
            toast.warn(t('toasts.aiQueryEmpty'));
            return;
        }
        try {
            const response = await aiService.generateModuleConfig(aiQuery);
            if (response.data && response.data.modules) {
                handleAiGeneratedModules(response.data.modules, response.data.notes);
                toast.success(response.data.notes || t('toasts.aiSuccess'));
            } else {
                toast.error(t('toasts.aiUnexpectedFormat'));
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || t('toasts.aiError');
            toast.error(errorMsg);
        }
    }, [handleAiGeneratedModules, state.projectObjects, state.floors, t]);
    
    const handleSaveProject = useCallback(() => {
        if (!validateProjectState(state.projectObjects, state.floors, t)) {
            return;
        }
        try {
            const projectData = { version: "1.0", ...state };
            const jsonString = JSON.stringify(projectData, null, 2);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `configurator_project_${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(t('toasts.projectSaved'));
        } catch (error) {
            toast.error(t('toasts.projectSaveError'));
        }
    }, [state, t]);

    const handleLoadProject = useCallback((event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const projectData = JSON.parse(e.target.result);
                    if (projectData.version === "1.0" && projectData.floors && projectData.projectObjects) {
                        // Очищаем данные от старых/некорректных полей которые могут конфликтовать
                        const sanitizedData = {
                            projectObjects: projectData.projectObjects || {},
                            floors: projectData.floors || [],
                            currentFloorId: projectData.currentFloorId,
                            selectedObjectId: projectData.selectedObjectId || null,
                            activeMode: projectData.activeMode || "modular",
                            clickedJointSegmentIndex: projectData.clickedJointSegmentIndex || null,
                            framelessElementsUnlocked: projectData.framelessElementsUnlocked || false,
                            notification: null, // Всегда сбрасываем уведомления
                        };
                        
                        dispatch({ type: 'SET_STATE', payload: sanitizedData });
                        toast.success(t('toasts.projectLoaded'));
                    } else {
                        toast.error(t('toasts.projectLoadInvalidFormat'));
                    }
                } catch (error) {
                    toast.error(t('toasts.projectLoadError'));
                }
            };
            reader.readAsText(file);
            event.target.value = null;
        }
    }, [dispatch, t]);

    const triggerLoadFile = useCallback(() => {
        loadFileInputRef.current?.click();
    }, []);

    const openTemplateSelectionPanel = useCallback((slotObject = {}) => {
        const slotData = slotObject.anchorModuleId 
            ? slotObject 
            : { 
                anchorModuleId: null, 
                anchorSide: 'bottom', // Произвольное значение, т.к. якоря нет
                x: slotObject.worldX || 0, 
                y: slotObject.worldY || 0 
              };

        setActivePlacementSlot(slotData);
        setIsTemplatePanelOpen(true);
        dispatch({ type: 'SET_SELECTED_OBJECT_ID', payload: null });
    }, [dispatch, setIsTemplatePanelOpen, setActivePlacementSlot]);

    return {
        loadFileInputRef,
        handleSaveProject,
        handleLoadProject,
        triggerLoadFile,
        submitAiQuery,
        openTemplateSelectionPanel,
        handleAiGeneratedModules,
        saveProjectToHistory,
    };
};